import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonButton,
  IonIcon,
  IonSearchbar,
  IonFab,
  IonFabButton,
  IonModal,
  IonInput,
  IonToast,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonList,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonActionSheet,
  IonAlert,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonChip,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonSpinner,
  IonSegment,
  IonSegmentButton,
  IonText,
} from '@ionic/react';
import {
  addOutline,
  ellipsisVertical,
  createOutline,
  banOutline,
  cartOutline,
  ticketOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  layersOutline,
  gridOutline,
  chevronDownOutline,
  chevronForwardOutline,
  alertCircleOutline,
  linkOutline,
  copyOutline,
  logoWhatsapp,
  addCircleOutline,
  trashOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { saleService, ticketService } from '../services/api';
import { Sale, Ticket, Event } from '../types';
import AddSaleModal from './AddSaleModal';
import FulfillmentModal from './FulfillmentModal';
import ConnectionDetailsModal from './ConnectionDetailsModal';
import AddTicketModal from './AddTicketModal';
import { IonToggle } from '@ionic/react';
import { eventBus, EVENTS } from '../utils/eventBus';
import {
  getCurrencySymbol,
  DEFAULT_CURRENCY,
} from '../utils/currencyConstants';
import { SPLIT_TYPE_OPTIONS } from '../utils/ticketConstants';
import { formatSplitDisplay } from '../utils/formatUtils';
import { generateWantedMessage } from '../utils/messageUtils';
import { fulfillmentService } from '../services/api';
import InlineEdit from './InlineEdit';
import SourceDetailsModal from './SourceDetailsModal';

interface SalesContentProps {
  event: Event;
}

const SalesContent: React.FC<SalesContentProps> = ({ event }) => {
  const { t } = useTranslation();
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewMode, setViewMode] = useState<'category' | 'block'>('category');
  const [showCancelledSales, setShowCancelledSales] = useState(false);
  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);
  const [showConnectionDetails, setShowConnectionDetails] = useState(false);
  const [connectionItemId, setConnectionItemId] = useState<string | null>(null);
  const [saleFulfillmentDetails, setSaleFulfillmentDetails] = useState<{
    [key: string]: any;
  }>({});
  const [showAddTicketModal, setShowAddTicketModal] = useState(false);
  const [saleForInventory, setSaleForInventory] = useState<Sale | null>(null);
  const [showSourceDetailsModal, setShowSourceDetailsModal] = useState(false);
  const [selectedSourceData, setSelectedSourceData] = useState<{
    type: 'supplier' | 'buyer' | 'site' | 'private';
    data: any;
  } | null>(null);

  useEffect(() => {
    loadSales();
  }, [event]);

  const handleSaleRowClick = (sale: Sale) => {
    const saleId = sale.id || sale._id;
    if (saleId) {
      setConnectionItemId(saleId);
      setShowConnectionDetails(true);
    }
  };

  useEffect(() => {
    // Subscribe to event bus for refreshing sales
    const unsubscribeSales = eventBus.on(EVENTS.REFRESH_SALES, () => {
      console.log('SalesContent: Refreshing sales due to email fetch');
      loadSales();
    });

    const unsubscribeSalesCreated = eventBus.on(
      EVENTS.SALES_CREATED_FROM_EMAIL,
      (data: { count: number }) => {
        setToastMessage(
          `${data.count} new sale${
            data.count > 1 ? 's' : ''
          } created from emails!`
        );
        setShowToast(true);
      }
    );

    const unsubscribeSaleUpdated = eventBus.on(
      EVENTS.SALE_UPDATED,
      (data: { saleId: string; ticketIds?: string[] }) => {
        console.log('SalesContent: Sale updated, refreshing sales');
        loadSales();
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeSales();
      unsubscribeSalesCreated();
      unsubscribeSaleUpdated();
    };
  }, []);

  const loadSales = async () => {
    try {
      setLoading(true);
      const data = await saleService.getAll({ eventId: event._id });
      const salesArray = Array.isArray(data) ? data : [];
      setSales(salesArray);

      // Load fulfillment details for completed sales
      const fulfillmentPromises = salesArray
        .filter((sale) => sale.status === 'confirmed')
        .map(async (sale) => {
          const saleId = sale.id || sale._id;
          if (saleId) {
            try {
              const details = await fulfillmentService.getSaleFulfillment(
                saleId
              );
              return { saleId, details };
            } catch (err) {
              console.error(
                `Failed to load fulfillment for sale ${saleId}:`,
                err
              );
              return { saleId, details: null };
            }
          }
          return null;
        });

      const fulfillmentResults = await Promise.all(fulfillmentPromises);
      const detailsMap: { [key: string]: any } = {};
      fulfillmentResults.forEach((result) => {
        if (result && result.saleId) {
          detailsMap[result.saleId] = result.details;
        }
      });
      setSaleFulfillmentDetails(detailsMap);
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadSales();
    event.detail.complete();
  };

  const handleSourceClick = (sale: Sale, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click from triggering
    
    // Determine source type and data
    if (sale.saleType === 'supplier' && sale.supplierId) {
      // Supplier sale
      setSelectedSourceData({
        type: 'supplier',
        data: sale.supplierId
      });
    } else if (sale.buyerName || sale.buyerEmail || sale.buyerPhone) {
      // Private sale with buyer details
      setSelectedSourceData({
        type: 'buyer',
        data: sale
      });
    } else if (sale.source && sale.source !== 'manual') {
      // Platform/site sale
      setSelectedSourceData({
        type: 'site',
        data: sale
      });
    } else {
      // Private/manual sale with no details
      setSelectedSourceData({
        type: 'private',
        data: null
      });
    }
    
    setShowSourceDetailsModal(true);
  };

  const handleCancelSale = async () => {
    if (!selectedSale) return;

    const saleId = selectedSale.id || selectedSale._id;
    if (!saleId) {
      setToastMessage('Cannot cancel sale: Invalid sale ID');
      setShowToast(true);
      return;
    }

    try {
      await saleService.cancel(saleId);
      setToastMessage(t('sales.cancelSuccess'));
      setShowToast(true);
      setShowCancelAlert(false);
      await loadSales();

      // Emit events to refresh other components
      eventBus.emit(EVENTS.REFRESH_SALES);
      eventBus.emit(EVENTS.REFRESH_TICKETS);
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const handleConfirmSale = async (sale: Sale) => {
    const saleId = sale.id || sale._id;
    if (!saleId) {
      setToastMessage('Cannot confirm sale: Invalid sale ID');
      setShowToast(true);
      return;
    }

    try {
      await saleService.update(saleId, { status: 'confirmed' });
      setToastMessage('Sale confirmed successfully');
      setShowToast(true);
      await loadSales();

      // Emit events to refresh other components
      eventBus.emit(EVENTS.REFRESH_SALES);
      eventBus.emit(EVENTS.REFRESH_TICKETS);
    } catch (err: any) {
      setToastMessage(err.message || 'Failed to confirm sale');
      setShowToast(true);
    }
  };

  const handleInlineSaleEdit = async (saleId: string, field: string, value: string | number) => {
    try {
      let processedValue = value;
      
      // Round sale price to 2 decimal places
      if (field === 'salePrice') {
        processedValue = Math.round(Number(value) * 100) / 100;
      }
      
      const updateData: any = { [field]: processedValue };
      
      // Backend will automatically recalculate totalAmount and netProfit
      await saleService.update(saleId, updateData);
      setToastMessage(`Sale ${field} updated successfully`);
      setShowToast(true);
      await loadSales();

      // Emit events to refresh other components
      eventBus.emit(EVENTS.REFRESH_SALES);
      if (field === 'quantity') {
        eventBus.emit(EVENTS.REFRESH_TICKETS);
      }
    } catch (err: any) {
      setToastMessage(err.message || `Failed to update ${field}`);
      setShowToast(true);
      // Reload sales to revert any optimistic updates
      await loadSales();
    }
  };

  const onSaleSuccess = async () => {
    setToastMessage(t('sales.saleRecorded'));
    setShowToast(true);
    await loadSales();

    // Notify that sales have been updated
    eventBus.emit(EVENTS.REFRESH_SALES);
  };

  const copyWantedMessage = async (sale: Sale) => {
    const message = generateWantedMessage({
      eventName: event.name,
      category: sale.category,
      block: sale.block,
      split: sale.split,
      quantity: sale.quantity || 1,
    });

    try {
      await navigator.clipboard.writeText(message);
      setToastMessage('Wanted message copied to clipboard');
      setShowToast(true);
    } catch (err) {
      console.error('Failed to copy:', err);
      setToastMessage('Failed to copy message');
      setShowToast(true);
    }
  };

  const copyStockSummary = async () => {
    try {
      let neededItems: string[] = [];
      let fulfilledItems: string[] = [];

      Object.entries(ticketRequirements).forEach(
        ([category, { fulfilled, unfulfilled }]) => {
          // Add unfulfilled items (needed)
          Object.entries(unfulfilled)
            .sort(([a], [b]) => {
              // Extract number from "X together" format or use predefined sizes
              const getSizeA = () => {
                if (a === 'singles') return 1;
                if (a === 'pairs') return 2;
                if (a === 'trios') return 3;
                if (a === 'quads') return 4;
                const match = a.match(/^(\d+) together$/);
                return match ? parseInt(match[1]) : 999;
              };
              const getSizeB = () => {
                if (b === 'singles') return 1;
                if (b === 'pairs') return 2;
                if (b === 'trios') return 3;
                if (b === 'quads') return 4;
                const match = b.match(/^(\d+) together$/);
                return match ? parseInt(match[1]) : 999;
              };
              return getSizeA() - getSizeB();
            })
            .forEach(([groupType, count]) => {
              if (count > 0) {
                neededItems.push(`${count} ${category} ${groupType}`);
              }
            });

          // Add fulfilled items
          Object.entries(fulfilled)
            .sort(([a], [b]) => {
              // Extract number from "X together" format or use predefined sizes
              const getSizeA = () => {
                if (a === 'singles') return 1;
                if (a === 'pairs') return 2;
                if (a === 'trios') return 3;
                if (a === 'quads') return 4;
                const match = a.match(/^(\d+) together$/);
                return match ? parseInt(match[1]) : 999;
              };
              const getSizeB = () => {
                if (b === 'singles') return 1;
                if (b === 'pairs') return 2;
                if (b === 'trios') return 3;
                if (b === 'quads') return 4;
                const match = b.match(/^(\d+) together$/);
                return match ? parseInt(match[1]) : 999;
              };
              return getSizeA() - getSizeB();
            })
            .forEach(([groupType, count]) => {
              if (count > 0) {
                fulfilledItems.push(`${count} ${category} ${groupType}`);
              }
            });
        }
      );

      const summary = [
        'Needed',
        '',
        ...neededItems,
        '',
        'fulfilled',
        '',
        ...fulfilledItems,
      ].join('\n');

      await navigator.clipboard.writeText(summary);
      setToastMessage('Stock summary copied to clipboard');
      setShowToast(true);
    } catch (err) {
      console.error('Failed to copy stock summary:', err);
      setToastMessage('Failed to copy stock summary');
      setShowToast(true);
    }
  };

  const sendWhatsAppMessage = (sale: Sale) => {
    const message = generateWantedMessage({
      eventName: event.name,
      category: sale.category,
      block: sale.block,
      split: sale.split,
      quantity: sale.quantity || 1,
    });

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const isSaleFulfilled = (sale: Sale): boolean => {
    const saleId = sale.id || sale._id || '';
    const fulfillmentDetails = saleFulfillmentDetails[saleId];

    if (fulfillmentDetails) {
      return fulfillmentDetails.isFullyFulfilled;
    }

    // Fallback to original logic
    return !!(sale.ticketIds && sale.ticketIds.length > 0);
  };

  const hasInventoryConnection = (sale: Sale): boolean => {
    const saleId = sale.id || sale._id || '';
    const fulfillmentDetails = saleFulfillmentDetails[saleId];
    
    return (fulfillmentDetails && fulfillmentDetails.totalFulfilled > 0) ||
           (sale.ticketIds && sale.ticketIds.length > 0);
  };

  const filteredSales = Array.isArray(sales)
    ? sales.filter((sale) => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        return (
          sale.buyerName?.toLowerCase().includes(searchLower) ||
          sale.buyerEmail?.toLowerCase().includes(searchLower) ||
          sale.buyerPhone?.toLowerCase().includes(searchLower)
        );
      })
    : [];

  // Separate sales by status
  const pendingSales = filteredSales.filter(
    (sale) => sale.status === 'pending'
  );
  const confirmedSales = filteredSales.filter(
    (sale) => sale.status === 'confirmed'
  );
  const cancelledSales = filteredSales.filter(
    (sale) => sale.status === 'cancelled'
  );

  // Calculate ticket requirements by category/block and split type
  const calculateTicketRequirements = () => {
    const requirements: {
      [key: string]: {
        fulfilled: { [groupSize: string]: number };
        unfulfilled: { [groupSize: string]: number };
      };
    } = {};

    // Only include confirmed sales for ticket requirements (exclude pending and cancelled)
    const confirmedSalesForRequirements = sales.filter(
      (sale) => sale.status === 'confirmed'
    );

    confirmedSalesForRequirements.forEach((sale) => {
      if (!sale.category || !sale.quantity) return;

      // Determine grouping key based on view mode
      let key: string;
      if (viewMode === 'category') {
        key = sale.category;
      } else {
        // Block view - include block in the key
        key = sale.block ? `${sale.category} - ${sale.block}` : sale.category;
      }

      if (!requirements[key]) {
        requirements[key] = {
          fulfilled: {},
          unfulfilled: {},
        };
      }

      const splitType = sale.split || 'all-together';
      const splitOption =
        SPLIT_TYPE_OPTIONS.find((opt) => opt.id === splitType) ||
        SPLIT_TYPE_OPTIONS[4];

      if (!splitOption) return;

      // Helper function to add tickets to groups
      const addTicketsToGroup = (
        targetGroup: { [key: string]: number },
        quantity: number
      ) => {
        let remaining = quantity;

        if (splitOption.maxGroup === -1) {
          // All together - use proper naming based on quantity
          const key =
            remaining === 1
              ? 'singles'
              : remaining === 2
              ? 'pairs'
              : remaining === 3
              ? 'trios'
              : remaining === 4
              ? 'quads'
              : `${remaining} together`;
          targetGroup[key] = (targetGroup[key] || 0) + 1;
        } else if (splitOption.maxGroup === 1) {
          // Singles only
          targetGroup['singles'] = (targetGroup['singles'] || 0) + remaining;
        } else {
          // Calculate groups based on max group size
          while (remaining > 0) {
            if (remaining >= splitOption.maxGroup) {
              const key =
                splitOption.maxGroup === 2
                  ? 'pairs'
                  : splitOption.maxGroup === 3
                  ? 'trios'
                  : splitOption.maxGroup === 4
                  ? 'quads'
                  : `${splitOption.maxGroup} together`;
              targetGroup[key] = (targetGroup[key] || 0) + 1;
              remaining -= splitOption.maxGroup;
            } else {
              // Handle remaining tickets
              const key =
                remaining === 1
                  ? 'singles'
                  : remaining === 2
                  ? 'pairs'
                  : remaining === 3
                  ? 'trios'
                  : remaining === 4
                  ? 'quads'
                  : `${remaining} together`;
              targetGroup[key] = (targetGroup[key] || 0) + 1;
              remaining = 0;
            }
          }
        }
      };

      // Check if sale is fulfilled using fulfillment details
      const saleId = sale.id || sale._id || '';
      const fulfillmentDetails = saleFulfillmentDetails[saleId];

      if (fulfillmentDetails) {
        const { totalFulfilled, totalNeeded } = fulfillmentDetails;
        if (totalFulfilled >= totalNeeded) {
          // Fully fulfilled
          addTicketsToGroup(requirements[key].fulfilled, sale.quantity);
        } else if (totalFulfilled > 0) {
          // Partially fulfilled - split between fulfilled and unfulfilled
          addTicketsToGroup(requirements[key].fulfilled, totalFulfilled);
          addTicketsToGroup(
            requirements[key].unfulfilled,
            totalNeeded - totalFulfilled
          );
        } else {
          // Not fulfilled at all
          addTicketsToGroup(requirements[key].unfulfilled, sale.quantity);
        }
      } else {
        // Fallback to original logic
        const isFulfilled = sale.ticketIds && sale.ticketIds.length > 0;
        const targetGroup = isFulfilled
          ? requirements[key].fulfilled
          : requirements[key].unfulfilled;
        addTicketsToGroup(targetGroup, sale.quantity);
      }
    });

    return requirements;
  };

  const ticketRequirements = calculateTicketRequirements();

  return (
    <div>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>

      <div className="px-4 mb-4">
        <IonSearchbar
          value={searchText}
          onIonChange={(e) => setSearchText(e.detail.value!)}
          placeholder={t('sales.searchSales')}
        />
      </div>

      {/* Ticket Requirements Card */}
      {Object.keys(ticketRequirements).length > 0 && (
        <div className="px-4 mb-4">
          <IonCard className="shadow-lg">
            <IonCardHeader className="bg-primary-50 dark:bg-primary-900/30">
              <div className="flex items-center justify-between">
                <IonCardTitle className="text-lg flex items-center">
                  <IonIcon icon={cartOutline} className="mr-2" />
                  Tickets Required to Purchase
                </IonCardTitle>
                <div className="flex items-center gap-2">
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={copyStockSummary}
                    title="Copy stock summary"
                  >
                    <IonIcon icon={copyOutline} />
                  </IonButton>
                  <IonSegment
                    value={viewMode}
                    onIonChange={(e) =>
                      setViewMode(e.detail.value as 'category' | 'block')
                    }
                    className="w-auto"
                    style={{ maxWidth: '280px' }}
                  >
                    <IonSegmentButton value="category">
                      <IonLabel className="text-xs">
                        <IonIcon icon={layersOutline} className="mr-1" />
                        Category
                      </IonLabel>
                    </IonSegmentButton>
                    <IonSegmentButton value="block">
                      <IonLabel className="text-xs">
                        <IonIcon icon={gridOutline} className="mr-1" />
                        Block
                      </IonLabel>
                    </IonSegmentButton>
                  </IonSegment>
                </div>
              </div>
            </IonCardHeader>
            <IonCardContent>
              <div className="space-y-4 mt-4">
                {Object.entries(ticketRequirements).map(
                  ([category, { fulfilled, unfulfilled }]) => {
                    const hasUnfulfilled = Object.keys(unfulfilled).length > 0;
                    const hasFulfilled = Object.keys(fulfilled).length > 0;

                    return (
                      <div
                        key={category}
                        className="border-l-4 border-primary-500 pl-4"
                      >
                        <h4 className="font-medium text-base mb-2 text-gray-900 dark:text-gray-100">
                          {category}
                        </h4>

                        {/* Unfulfilled (still need to buy) */}
                        {hasUnfulfilled && (
                          <div className="mb-3">
                            <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-1">
                              Still need to purchase:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(unfulfilled)
                                .sort(([a], [b]) => {
                                  // Extract number from "X together" format or use predefined sizes
                                  const getSizeA = () => {
                                    if (a === 'singles') return 1;
                                    if (a === 'pairs') return 2;
                                    if (a === 'trios') return 3;
                                    if (a === 'quads') return 4;
                                    const match = a.match(/^(\d+) together$/);
                                    return match ? parseInt(match[1]) : 999;
                                  };
                                  const getSizeB = () => {
                                    if (b === 'singles') return 1;
                                    if (b === 'pairs') return 2;
                                    if (b === 'trios') return 3;
                                    if (b === 'quads') return 4;
                                    const match = b.match(/^(\d+) together$/);
                                    return match ? parseInt(match[1]) : 999;
                                  };
                                  return getSizeA() - getSizeB();
                                })
                                .map(([groupType, count]) => (
                                  <IonChip
                                    key={`${category}-unfulfilled-${groupType}`}
                                    color="danger"
                                  >
                                    <IonIcon icon={ticketOutline} />
                                    <IonLabel className="font-medium">
                                      {count} × {groupType}
                                    </IonLabel>
                                  </IonChip>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Fulfilled (already purchased from inventory) */}
                        {hasFulfilled && (
                          <div>
                            <p className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">
                              Already fulfilled:
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(fulfilled)
                                .sort(([a], [b]) => {
                                  // Extract number from "X together" format or use predefined sizes
                                  const getSizeA = () => {
                                    if (a === 'singles') return 1;
                                    if (a === 'pairs') return 2;
                                    if (a === 'trios') return 3;
                                    if (a === 'quads') return 4;
                                    const match = a.match(/^(\d+) together$/);
                                    return match ? parseInt(match[1]) : 999;
                                  };
                                  const getSizeB = () => {
                                    if (b === 'singles') return 1;
                                    if (b === 'pairs') return 2;
                                    if (b === 'trios') return 3;
                                    if (b === 'quads') return 4;
                                    const match = b.match(/^(\d+) together$/);
                                    return match ? parseInt(match[1]) : 999;
                                  };
                                  return getSizeA() - getSizeB();
                                })
                                .map(([groupType, count]) => (
                                  <IonChip
                                    key={`${category}-fulfilled-${groupType}`}
                                    color="success"
                                  >
                                    <IonIcon icon={ticketOutline} />
                                    <IonLabel className="font-medium">
                                      {count} × {groupType} ✓
                                    </IonLabel>
                                  </IonChip>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="space-y-1">
                  {(() => {
                    let totalUnfulfilled = 0;
                    let totalFulfilled = 0;

                    Object.values(ticketRequirements).forEach(
                      ({ unfulfilled, fulfilled }) => {
                        const calculateTotal = (groups: any) => {
                          return Object.entries(groups).reduce(
                            (total, [groupType, count]) => {
                              const match = groupType.match(/^(\d+) together$/);
                              if (match) {
                                return (
                                  total + parseInt(match[1]) * (count as number)
                                );
                              } else if (groupType === 'singles') {
                                return total + (count as number);
                              } else if (groupType === 'pairs') {
                                return total + 2 * (count as number);
                              } else if (groupType === 'trios') {
                                return total + 3 * (count as number);
                              } else if (groupType === 'quads') {
                                return total + 4 * (count as number);
                              }
                              return total;
                            },
                            0
                          );
                        };

                        totalUnfulfilled += calculateTotal(unfulfilled);
                        totalFulfilled += calculateTotal(fulfilled);
                      }
                    );

                    return (
                      <>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Summary:</strong>
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                          <strong>Still need to purchase:</strong>{' '}
                          {totalUnfulfilled} tickets
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          <strong>Already fulfilled:</strong> {totalFulfilled}{' '}
                          tickets
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Total sales:</strong>{' '}
                          {totalUnfulfilled + totalFulfilled} tickets across{' '}
                          {Object.keys(ticketRequirements).length}{' '}
                          {viewMode === 'category' ? 'categories' : 'groups'}
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      )}

      <div className="px-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <IonSpinner name="crescent" className="w-8 h-8" color="primary" />
          </div>
        ) : pendingSales.length === 0 &&
          confirmedSales.length === 0 &&
          cancelledSales.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {searchText ? t('common.noResultsFound') : t('sales.noSales')}
            </p>
          </div>
        ) : (
          <>
            {/* Pending Sales Table */}
            {pendingSales.length > 0 && (
              <IonCard className="shadow-lg mb-4">
                <div className="bg-yellow-100 dark:bg-yellow-900/20 px-4 py-2">
                  <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
                    Pending Sales ({pendingSales.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.saleDate')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.orderNumber')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.category')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.block')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.restrictions')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.split')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.quantity')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.salePrice')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.totalAmount')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.netProfit')}
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('common.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {pendingSales.map((sale, index) => {
                        return (
                          <tr
                            key={`pending-${index}`}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {sale.saleDate
                                ? new Date(sale.saleDate).toLocaleDateString()
                                : '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {sale.orderNumber || '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {sale.category || '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {sale.block || '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {sale.restrictions || '-'}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              <InlineEdit
                                type="select"
                                value={sale.split || 'together'}
                                options={SPLIT_TYPE_OPTIONS}
                                onSave={(value) => handleInlineSaleEdit(sale.id || sale._id || '', 'split', value)}
                                selectLabel="Select split type"
                              />
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              <InlineEdit
                                type="number"
                                value={sale.quantity || 1}
                                onSave={(value) => handleInlineSaleEdit(sale.id || sale._id || '', 'quantity', value)}
                                placeholder="Quantity"
                              />
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              £<InlineEdit
                                type="number"
                                value={sale.salePrice || 0}
                                onSave={(value) => handleInlineSaleEdit(sale.id || sale._id || '', 'salePrice', value)}
                                placeholder="Price"
                                isPrice={true}
                              />
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              £{sale.totalAmount}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              £{sale.netProfit}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end space-x-2">
                                <IonButton
                                  fill="clear"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSale(sale);
                                    setShowActionSheet(true);
                                  }}
                                >
                                  <IonIcon icon={ellipsisVertical} />
                                </IonButton>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </IonCard>
            )}

            {/* Confirmed Sales Table */}
            {confirmedSales.length > 0 && (
              <IonCard className="shadow-lg mb-4">
                <div className="bg-green-100 dark:bg-green-900/20 px-4 py-2">
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                    Confirmed Sales ({confirmedSales.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.saleDate')}
                        </th>
                        {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.buyerName')}
                        </th> */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.orderNumber')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.category')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.block')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.restrictions')}
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.split')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('inventory.quantity')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.salePrice')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.totalAmount')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.netProfit')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('sales.source')}
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Fulfillment
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Delivery
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t('common.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {confirmedSales.map((sale, index) => {
                        const saleKey =
                          sale.id || sale._id || `confirmed-${index}`;
                        return (
                          <tr
                            key={saleKey}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                            onClick={() => handleSaleRowClick(sale)}
                            title="Click to view connection details"
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className=" flex flex-col text-sm text-gray-900 dark:text-gray-100">
                                {format(
                                  new Date(sale.saleDate),
                                  'MMM dd, yyyy'
                                )}
                                <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">
                                  {format(new Date(sale.saleDate), 'HH:mm')}
                                </span>
                              </div>
                            </td>
                            {/* <td className="px-4 py-3 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {sale.saleType === 'supplier' &&
                                  sale.supplierId
                                    ? typeof sale.supplierId === 'object'
                                      ? sale.supplierId.name
                                      : 'Supplier'
                                    : sale.buyerName}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {sale.saleType === 'supplier' ? (
                                    <span className="text-yellow-600 dark:text-yellow-400">
                                      Supplier Sale
                                    </span>
                                  ) : (
                                    sale.buyerEmail
                                  )}
                                </div>
                              </div>
                            </td> */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {sale.orderNumber || '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {sale.category ? sale.category : '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {sale.block || '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {sale.restrictions || '-'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                <InlineEdit
                                  type="select"
                                  value={sale.split || 'together'}
                                  options={SPLIT_TYPE_OPTIONS}
                                  onSave={(value) => handleInlineSaleEdit(saleKey, 'split', value)}
                                  selectLabel="Select split type"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                <InlineEdit
                                  type="number"
                                  value={sale.quantity || sale.tickets?.length || sale.ticketIds?.length || 1}
                                  onSave={(value) => handleInlineSaleEdit(saleKey, 'quantity', value)}
                                  placeholder="Quantity"
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {getCurrencySymbol(
                                  sale.currency || DEFAULT_CURRENCY
                                )}
                                <InlineEdit
                                  type="number"
                                  value={sale.salePrice || 0}
                                  onSave={(value) => handleInlineSaleEdit(saleKey, 'salePrice', value)}
                                  placeholder="Price"
                                  isPrice={true}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {getCurrencySymbol(
                                  sale.currency || DEFAULT_CURRENCY
                                )}
                                {sale.totalAmount.toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              {hasInventoryConnection(sale) ? (
                                <div className="text-sm font-medium text-green-600 dark:text-green-400">
                                  {getCurrencySymbol(sale.currency || DEFAULT_CURRENCY)}
                                  {sale.netProfit.toFixed(2)}
                                </div>
                              ) : (
                                <div className="text-sm font-medium text-gray-400 dark:text-gray-500">
                                  <span title="Profit not calculated - no inventory connected">
                                    —
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
                                  sale.source === 'livefootballtickets'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : sale.source === 'stubhub'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                    : sale.source === 'footballticketnet'
                                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                                    : sale.source === 'ticombo'
                                    ? 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200'
                                    : sale.source === 'fanpass'
                                    ? 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200'
                                    : sale.source === 'tixstock'
                                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}
                                onClick={(e) => handleSourceClick(sale, e)}
                                title="Click to view source details"
                              >
                                {sale.source || 'manual'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              {(() => {
                                const saleId = sale.id || sale._id || '';
                                const fulfillmentDetails =
                                  saleFulfillmentDetails[saleId];

                                if (fulfillmentDetails) {
                                  const {
                                    totalFulfilled,
                                    totalNeeded,
                                    isFullyFulfilled,
                                  } = fulfillmentDetails;

                                  if (isFullyFulfilled) {
                                    return (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                        <IonIcon
                                          icon={checkmarkCircleOutline}
                                          className="mr-1"
                                        />
                                        Fulfilled
                                      </span>
                                    );
                                  } else if (totalFulfilled > 0) {
                                    return (
                                      <div>
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                          <IonIcon
                                            icon={alertCircleOutline}
                                            className="mr-1"
                                          />
                                          Partial
                                        </span>
                                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                          {totalFulfilled} of {totalNeeded}{' '}
                                          tickets
                                        </div>
                                      </div>
                                    );
                                  }
                                }

                                // Fallback to original logic if no fulfillment details
                                if (
                                  sale.ticketIds &&
                                  sale.ticketIds.length > 0
                                ) {
                                  return (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                      <IonIcon
                                        icon={checkmarkCircleOutline}
                                        className="mr-1"
                                      />
                                      Fulfilled
                                    </span>
                                  );
                                } else {
                                  return (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                      <IonIcon
                                        icon={closeCircleOutline}
                                        className="mr-1"
                                      />
                                      Pending
                                    </span>
                                  );
                                }
                              })()}
                            </td>
                            <td
                              className="px-4 py-3 whitespace-nowrap text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IonToggle
                                checked={sale.deliveryStatus === 'delivered'}
                                onIonChange={async (e) => {
                                  try {
                                    const newStatus:
                                      | 'delivered'
                                      | 'not delivered' = e.detail.checked
                                      ? 'delivered'
                                      : 'not delivered';
                                    const saleId = sale.id || sale._id || '';

                                    await saleService.update(saleId, {
                                      deliveryStatus: newStatus,
                                    });

                                    // Update local state - use the specific saleKey for comparison
                                    const updatedSales = sales.map((s) => {
                                      const currentSaleId = s.id || s._id || '';
                                      if (currentSaleId === saleId) {
                                        return {
                                          ...s,
                                          deliveryStatus: newStatus as
                                            | 'delivered'
                                            | 'not delivered',
                                        };
                                      }
                                      return s;
                                    });

                                    setSales(updatedSales);
                                    setToastMessage(
                                      `Sale marked as ${newStatus}`
                                    );
                                    setShowToast(true);
                                  } catch (error) {
                                    console.error(
                                      'Failed to update delivery status:',
                                      error
                                    );
                                    setToastMessage(
                                      'Failed to update delivery status'
                                    );
                                    setShowToast(true);
                                  }
                                }}
                                color="success"
                              />
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {sale.deliveryStatus === 'delivered'
                                  ? 'Delivered'
                                  : 'Not Delivered'}
                              </div>
                            </td>
                            <td
                              className="whitespace-nowrap text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center space-x-1">
                                {!isSaleFulfilled(sale) &&
                                  sale.status === 'confirmed' && (
                                    <>
                                      <IonButton
                                        fill="clear"
                                        size="small"
                                        onClick={() => copyWantedMessage(sale)}
                                        title="Copy wanted message"
                                      >
                                        <IonIcon icon={copyOutline} />
                                      </IonButton>
                                      <IonButton
                                        fill="clear"
                                        size="small"
                                        onClick={() =>
                                          sendWhatsAppMessage(sale)
                                        }
                                        title="Send via WhatsApp"
                                      >
                                        <IonIcon icon={logoWhatsapp} />
                                      </IonButton>
                                    </>
                                  )}
                                <IonButton
                                  fill="clear"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSale(sale);
                                    setShowActionSheet(true);
                                  }}
                                  disabled={sale.status === 'cancelled'}
                                >
                                  <IonIcon icon={ellipsisVertical} />
                                </IonButton>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </IonCard>
            )}

            {/* Cancelled Sales Section */}
            {cancelledSales.length > 0 && (
              <IonCard className="shadow-lg">
                <IonCardHeader
                  className="bg-red-50 dark:bg-red-900/20 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  onClick={() => setShowCancelledSales(!showCancelledSales)}
                >
                  <div className="flex items-center justify-between">
                    <IonCardTitle className="text-lg flex items-center">
                      <IonIcon
                        icon={
                          showCancelledSales
                            ? chevronDownOutline
                            : chevronForwardOutline
                        }
                        className="mr-2"
                      />
                      <IonIcon
                        icon={alertCircleOutline}
                        className="mr-2 text-red-600"
                      />
                      Cancelled Sales
                      <IonChip color="danger" className="ml-2">
                        {cancelledSales.length}
                      </IonChip>
                    </IonCardTitle>
                    <IonText className="text-sm text-gray-500">
                      Click to {showCancelledSales ? 'collapse' : 'expand'}
                    </IonText>
                  </div>
                </IonCardHeader>

                {showCancelledSales && (
                  <IonCardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('sales.saleDate')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('sales.buyerName')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('sales.category')}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('inventory.quantity')}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('sales.totalAmount')}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Cancelled On
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {cancelledSales.map((sale, index) => {
                            const saleKey =
                              sale.id || sale._id || `cancelled-sale-${index}`;
                            return (
                              <tr
                                key={saleKey}
                                className="hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors opacity-75"
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-gray-100">
                                    {format(
                                      new Date(sale.saleDate),
                                      'MMM dd, yyyy'
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-gray-100">
                                    {sale.buyerName}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-gray-100">
                                    {sale.category || '-'}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  <div className="text-sm text-gray-900 dark:text-gray-100">
                                    {sale.quantity || 1}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  <div className="text-sm text-gray-900 dark:text-gray-100 line-through">
                                    {getCurrencySymbol(
                                      sale.currency || DEFAULT_CURRENCY
                                    )}
                                    {sale.totalAmount.toFixed(2)}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  <div className="text-sm text-red-600 dark:text-red-400">
                                    {sale.cancelledAt
                                      ? format(
                                          new Date(sale.cancelledAt),
                                          'MMM dd, yyyy HH:mm'
                                        )
                                      : 'N/A'}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </IonCardContent>
                )}
              </IonCard>
            )}
          </>
        )}
      </div>

      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonFabButton onClick={() => setShowAddModal(true)}>
          <IonIcon icon={addOutline} />
        </IonFabButton>
      </IonFab>

      {/* Record Sale Modal */}
      <AddSaleModal
        isOpen={showAddModal}
        onDismiss={() => setShowAddModal(false)}
        event={event}
        onSuccess={onSaleSuccess}
      />

      {/* Action Sheet */}
      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => {
          setShowActionSheet(false);
          // Don't clear selectedSale here as we need it for the modal
        }}
        buttons={[
          // Only show inventory options for confirmed sales
          ...(selectedSale?.status === 'confirmed'
            ? [
                {
                  text: 'Add Inventory',
                  icon: addCircleOutline,
                  handler: () => {
                    if (selectedSale) {
                      setSaleForInventory(selectedSale);
                      setShowActionSheet(false);
                      setTimeout(() => {
                        setShowAddTicketModal(true);
                      }, 300);
                    }
                    return true;
                  },
                },
                {
                  text: 'Link Inventory',
                  icon: linkOutline,
                  handler: () => {
                    setShowActionSheet(false);
                    setTimeout(() => {
                      setShowFulfillmentModal(true);
                    }, 300);
                    return true;
                  },
                },
              ]
            : []),
          // Show confirm option for pending sales
          ...(selectedSale?.status === 'pending'
            ? [
                {
                  text: 'Confirm Sale',
                  icon: checkmarkCircleOutline,
                  handler: () => {
                    if (selectedSale) {
                      handleConfirmSale(selectedSale);
                    }
                    setShowActionSheet(false);
                    return true;
                  },
                },
              ]
            : []),
          {
            text: t('common.edit'),
            icon: createOutline,
            handler: () => {
              setShowEditModal(true);
              return;
            },
          },
          {
            text: t('sales.cancel'),
            icon: banOutline,
            role: 'destructive',
            handler: () => {
              setShowCancelAlert(true);
              return;
            },
          },
          {
            text: t('common.cancel'),
            role: 'cancel',
          },
        ]}
      />

      {/* Cancel Alert */}
      <IonAlert
        isOpen={showCancelAlert}
        onDidDismiss={() => setShowCancelAlert(false)}
        header={t('common.confirm')}
        message={t('sales.cancelConfirm')}
        buttons={[
          {
            text: t('common.cancel'),
            role: 'cancel',
          },
          {
            text: t('sales.cancel'),
            handler: handleCancelSale,
          },
        ]}
      />

      {/* Edit Sale Modal */}
      <AddSaleModal
        isOpen={showEditModal}
        onDismiss={() => {
          setShowEditModal(false);
          setSelectedSale(null);
        }}
        event={event}
        sale={selectedSale}
        onSuccess={async () => {
          setToastMessage(t('sales.updateSuccess'));
          setShowToast(true);
          setShowEditModal(false);
          setSelectedSale(null);
          await loadSales();

          // Notify that sales have been updated
          eventBus.emit(EVENTS.REFRESH_SALES);
        }}
      />

      {/* Toast */}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
      />

      {/* Fulfillment Modal */}
      <FulfillmentModal
        isOpen={showFulfillmentModal}
        onDismiss={() => {
          setShowFulfillmentModal(false);
          setSelectedSale(null);
        }}
        sale={selectedSale}
        onSuccess={async () => {
          setToastMessage('Successfully linked inventory to sale');
          setShowToast(true);
          setShowFulfillmentModal(false);
          setSelectedSale(null);
          await loadSales();

          // Notify that sales have been updated (affects net profit)
          eventBus.emit(EVENTS.REFRESH_SALES);
        }}
      />

      {/* Connection Details Modal */}
      <ConnectionDetailsModal
        isOpen={showConnectionDetails}
        onDismiss={() => {
          setShowConnectionDetails(false);
          setConnectionItemId(null);
        }}
        type="sale"
        itemId={connectionItemId}
      />

      {/* Add Ticket Modal for adding inventory from sale */}
      <AddTicketModal
        isOpen={showAddTicketModal}
        onDismiss={() => {
          setShowAddTicketModal(false);
          setSaleForInventory(null);
          setSelectedSale(null);
        }}
        event={event}
        saleItem={saleForInventory}
        onSuccess={async () => {
          setToastMessage('Inventory added and linked to sale successfully');
          setShowToast(true);
          setShowAddTicketModal(false);
          setSaleForInventory(null);
          setSelectedSale(null);
          // Refresh sales to show updated fulfillment
          await loadSales();
          // Emit events to refresh other components
          eventBus.emit(EVENTS.REFRESH_TICKETS);
          eventBus.emit(EVENTS.REFRESH_SALES);
        }}
      />

      {/* Source Details Modal */}
      <SourceDetailsModal
        isOpen={showSourceDetailsModal}
        onClose={() => {
          setShowSourceDetailsModal(false);
          setSelectedSourceData(null);
        }}
        sourceType={selectedSourceData?.type || 'private'}
        sourceData={selectedSourceData?.data || null}
        context="sale"
      />
    </div>
  );
};

export default SalesContent;
